import { externalUrlHref } from "@/utils/external-url";

export interface FreelancerCopyData {
  nome?: string | null;
  pretensao_salarial?: string | number | null;
  areas?: string[] | string | null;
  cidade?: string | null;
  city?: string | null;
  state?: string | null;
  portfolio_url?: string | null;
  linkedin_url?: string | null;
}

const normalize = (value: string | null | undefined): string | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
};

const formatAreas = (areas: FreelancerCopyData["areas"]): string | null => {
  if (!areas) return null;
  if (Array.isArray(areas)) {
    const filtered = areas
      .map((a) => normalize(a))
      .filter((a): a is string => !!a);
    return filtered.length > 0 ? filtered.join(", ") : null;
  }
  return normalize(areas);
};

const formatLocation = (data: FreelancerCopyData): string | null => {
  const cidade = normalize(data.cidade);
  if (cidade) return cidade;
  const city = normalize(data.city);
  const state = normalize(data.state);
  if (city && state) return `${city}/${state}`;
  return city ?? state ?? null;
};

const formatPretensao = (
  value: FreelancerCopyData["pretensao_salarial"],
): string | null => {
  if (value == null) return null;
  if (typeof value === "number") {
    if (!Number.isFinite(value) || value <= 0) return null;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  }
  return normalize(value);
};

export function formatFreelancersCopyText(list: FreelancerCopyData[]): string {
  return list
    .map((item) => formatFreelancerCopyText(item))
    .filter((text) => text.length > 0)
    .join("\n\n");
}

export function formatFreelancerCopyText(data: FreelancerCopyData): string {
  const lines: string[] = [];
  const nome = normalize(data.nome);
  if (nome) lines.push(nome);

  const pretensao = formatPretensao(data.pretensao_salarial);
  if (pretensao) lines.push(`Pretensão: ${pretensao}`);

  const areas = formatAreas(data.areas);
  if (areas) lines.push(areas);

  const localizacao = formatLocation(data);
  if (localizacao) lines.push(localizacao);

  // Só links navegáveis: URL quebrada colada no Slack é pior que ausente.
  const portfolio = externalUrlHref(data.portfolio_url);
  if (portfolio) lines.push(portfolio);

  const linkedin = externalUrlHref(data.linkedin_url);
  if (linkedin) lines.push(linkedin);

  return lines.join("\n");
}
